import { GetStaticProps } from "next";
import Head from "next/head";
import { useMemo, useState } from "react";
import { HStack, VStack, Spacing } from "goi_common";

import allParlors from "@/constants/parlors_detail.json";
import { BigParlor } from "@/containers/HomeContainer/components/BigParlor";
import { HomeHeroSection } from "@/containers/HomeContainer/components/HomeHeroSection";
import {
  HomeRegionFilter,
  getRegionShort,
} from "@/containers/HomeContainer/components/HomeRegionFilter";
import { HomeSearchBar } from "@/containers/HomeContainer/components/HomeSearchBar";
import { GoiHeader } from "@/containers/HomeContainer/components/GoiHeader";
import { HEADER_NAV_HEIGHT } from "@/components/HeaderNav";
import { HEADER_HEIGHT } from "@/constants/layout";

type ParlorSummary = {
  id: number;
  companyname: string;
  address: string;
  managed_type: "대학병원" | "공설" | "전문";
  binso_average_charge: number;
  imageUrl?: string | null;
};

export const getStaticProps: GetStaticProps = async () => {
  const description =
    "전국 1,100개 이상의 장례식장 비용·위치·빈소 정보를 한눈에 확인하세요. 고이장례연구소가 24시간 무료 장례 상담과 투명한 견적을 안내합니다.";

  const activeParlors = (allParlors as any[]).filter(
    (p) => p.is_excluded !== true
  );

  const sortByImageAndCharge = (a: any, b: any) => {
    const aHasImg = a.images.length > 0 ? 1 : 0;
    const bHasImg = b.images.length > 0 ? 1 : 0;
    if (bHasImg !== aHasImg) return bHasImg - aHasImg;
    return (b.binso_average_charge || 0) - (a.binso_average_charge || 0);
  };

  const toSummary = (p: any): ParlorSummary => ({
    id: p.id,
    companyname: p.companyname,
    address: p.address,
    managed_type: p.managed_type,
    binso_average_charge: p.binso_average_charge,
    imageUrl: p.images[0]?.url ?? null,
  });

  const topParlors = activeParlors
    .sort(sortByImageAndCharge)
    .slice(0, 30)
    .map(toSummary);

  const allParlorsSummary: ParlorSummary[] = activeParlors
    .sort(sortByImageAndCharge)
    .map(toSummary);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://parlor.goifuneral.co.kr/#website",
        url: "https://parlor.goifuneral.co.kr/",
        name: "고이 장례식장 정보",
        description,
        inLanguage: "ko",
      },
      {
        "@type": "Organization",
        "@id": "https://parlor.goifuneral.co.kr/#organization",
        name: "고이 장례식장 정보",
        url: "https://parlor.goifuneral.co.kr/",
        sameAs: [
          "https://blog.naver.com/PostList.naver?blogId=goifuneral",
          "https://www.youtube.com/c/고이TV알기쉬운장례정보",
          "https://www.instagram.com/goi.funeral/",
        ],
      },
      {
        "@type": "ItemList",
        name: "전국 장례식장 목록",
        itemListElement: topParlors.slice(0, 10).map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://parlor.goifuneral.co.kr/place/${p.id}/family/main/`,
          name: p.companyname,
        })),
      },
    ],
  };

  return {
    props: {
      topParlors,
      allParlorsSummary,
      pageDescription: description,
      pageKeywords:
        "장례식장, 장례식장 정보, 장례식장 비용, 장례식장 위치, 장례식장 빈소 현황, 교통편 안내, 전국 장례식장, 장례 상담, 무료 장례 상담",
      pageOgTitle: "전국 장례식장 정보 제공 | 고이 장례식장 정보",
      pageOgDescription: description,
      pageCanonical: "https://parlor.goifuneral.co.kr/",
      pageJsonLd: JSON.stringify(jsonLd),
    },
  };
};

interface HomeProps {
  topParlors: ParlorSummary[];
  allParlorsSummary: ParlorSummary[];
  pageJsonLd?: string;
}

const Home = ({ topParlors, allParlorsSummary, pageJsonLd }: HomeProps) => {
  const [activeRegion, setActiveRegion] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const displayedParlors = useMemo(() => {
    const hasFilter = activeRegion !== null || searchQuery.trim() !== "";
    const source = hasFilter ? allParlorsSummary : topParlors;

    return source.filter((p) => {
      const matchRegion = activeRegion
        ? getRegionShort(p.address) === activeRegion
        : true;
      const matchSearch = searchQuery.trim()
        ? p.companyname.includes(searchQuery.trim())
        : true;
      return matchRegion && matchSearch;
    });
  }, [activeRegion, searchQuery, topParlors, allParlorsSummary]);

  return (
    <>
      {pageJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageJsonLd }}
          />
        </Head>
      )}
      <VStack width="100%" alignItems="center">
        <GoiHeader />
        <VStack width="100%" css={{ maxWidth: "480px" }}>
          {/* GoiHeader 는 position:fixed 라 본문을 밀지 않는다.
              로고 행(HEADER_HEIGHT) + Nav 행(HEADER_NAV_HEIGHT) 만큼 비운다 */}
          <Spacing gap={HEADER_HEIGHT + HEADER_NAV_HEIGHT} />
          <HomeHeroSection />
          <HomeRegionFilter
            activeRegion={activeRegion}
            onRegionChange={setActiveRegion}
          />
          <HomeSearchBar value={searchQuery} onChange={setSearchQuery} />
          <Spacing gap={12} />
          {displayedParlors.length === 0 ? (
            <VStack alignItems="center" padding="40px 20px">
              <div className="body3_r gray500">
                {searchQuery
                  ? `"${searchQuery}"에 해당하는 장례식장이 없습니다.`
                  : "해당 지역에 등록된 장례식장이 없습니다."}
              </div>
            </VStack>
          ) : (
            <HStack gap="20px" padding="0 16px" css={{ flexWrap: "wrap" }}>
              {displayedParlors.map((p) => (
                <BigParlor
                  href={`/place/${p.id}/family/main/`}
                  key={p.id}
                  src={p.imageUrl}
                  name={p.companyname}
                  address={p.address}
                  price={p.binso_average_charge}
                  managed_type={p.managed_type}
                />
              ))}
            </HStack>
          )}
        </VStack>
        <Spacing gap={40} />
      </VStack>
    </>
  );
};

export default Home;
